import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import Sidebar from "../components/Sidebar";
import { Bars3Icon } from "@heroicons/react/24/solid";
import TableHeaderFilter from "../components/TableHeaderfilter";
import SearchableDropdown from "../components/SearchableDropdown";
import AddEmployeeModal from "../components/AddEmployeeModal";
import ExcelExport from "../components/ExcelExport";
import { UserGroupIcon } from "@heroicons/react/24/solid";
import { PlusIcon } from "@heroicons/react/24/outline";

/* ── Tab config ─────────────────────────────────────────────────────────── */
const TABS = [
  {
    id: "all",
    label: "ALL",
    active: "bg-blue-50 text-blue-700 border-blue-200",
    idle: "text-slate-500 border-transparent hover:bg-slate-100 hover:text-slate-700",
  },
  {
    id: "active",
    label: "ACTIVE",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    idle: "text-slate-500 border-transparent hover:bg-emerald-50 hover:text-emerald-600",
  },
  {
    id: "inactive",
    label: "INACTIVE",
    active: "bg-rose-50 text-rose-700 border-rose-200",
    idle: "text-slate-500 border-transparent hover:bg-rose-50 hover:text-rose-600",
  },
];

/* ── Stat pill (mirrors AdminAttendanceOverview StatCard) ────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
  <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
    <span className={`w-2 h-2 rounded-full ${dotClass}`} />
    {label} <span className="font-extrabold">{value}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const AdminEmployees = () => {
  const [employees, setEmployees]               = useState([]);
  const [activeTab, setActiveTab]               = useState("all");
  const [search, setSearch]                     = useState("");
  const [showModal, setShowModal]               = useState({ open: false, data: null });
  const [loading, setLoading]                   = useState(true);
  const [filterEmployeeId, setFilterEmployeeId] = useState([]);
  const [filterEmployeeName, setFilterEmployeeName] = useState([]);
  const [filterEmail, setFilterEmail]           = useState([]);
  const [filterJobRole, setFilterJobRole]       = useState([]);
  const [sidebarOpen, setSidebarOpen]           = useState(false);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER_URL}/Rahul/fetchAllEmployeeDetails.php`);
      if (response.data.status === true) {
        setEmployees(response.data.data);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const status     = Number(emp.is_active) === 1 ? "active" : "inactive";
      const tabMatch   = activeTab === "all" ? true : status === activeTab;
      const searchLower = search.toLowerCase();
      const searchMatch =
        emp.employee_name?.toLowerCase().includes(searchLower) ||
        emp.employee_id?.toLowerCase().includes(searchLower) ||
        emp.job_role?.toLowerCase().includes(searchLower) ||
        emp.email?.toLowerCase().includes(searchLower);
      const idMatch    = filterEmployeeId.length   === 0 || filterEmployeeId.includes(emp.employee_id);
      const nameMatch  = filterEmployeeName.length === 0 || filterEmployeeName.includes(emp.employee_name);
      const emailMatch = filterEmail.length         === 0 || filterEmail.includes(emp.email);
      const roleMatch  = filterJobRole.length       === 0 || filterJobRole.includes(emp.job_role);
      return tabMatch && searchMatch && idMatch && nameMatch && emailMatch && roleMatch;
    });
  }, [employees, activeTab, search, filterEmployeeId, filterEmployeeName, filterEmail, filterJobRole]);

  const employeeIdOptions   = useMemo(() => [...new Set(employees.map(e => e.employee_id))].filter(Boolean).map(v => ({ label: v, value: v })),   [employees]);
  const employeeNameOptions = useMemo(() => [...new Set(employees.map(e => e.employee_name))].filter(Boolean).map(v => ({ label: v, value: v })), [employees]);
  const emailOptions        = useMemo(() => [...new Set(employees.map(e => e.email))].filter(Boolean).map(v => ({ label: v, value: v })),         [employees]);
  const jobRoleOptions      = useMemo(() => [...new Set(employees.map(e => e.job_role))].filter(Boolean).map(v => ({ label: v, value: v })),      [employees]);

  /* summary counts */
  const activeCount   = useMemo(() => employees.filter(e => Number(e.is_active) === 1).length,  [employees]);
  const inactiveCount = useMemo(() => employees.filter(e => Number(e.is_active) !== 1).length,  [employees]);

  const toggleStatus = async (employee_id, currentIsActive) => {
    const newIsActive = Number(currentIsActive) === 1 ? 0 : 1;
    try {
      const response = await axios.post(
        `${SERVER_URL}/Rahul/updateEmployeeDetails.php`,
        { employee_id, is_active: newIsActive, is_delete: 0 },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data.status === true) {
        setEmployees((prev) =>
          prev.map((emp) => emp.employee_id === employee_id ? { ...emp, is_active: newIsActive } : emp)
        );
      } else {
        alert(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Failed to update employee");
    }
  };

  const excelData = useMemo(() =>
    filteredEmployees.map(emp => ({ ...emp, is_active: Number(emp.is_active) === 1 ? "ACTIVE" : "DEACTIVE" })),
    [filteredEmployees]
  );

  const excelColumns = [
    { header: "Employee ID",    key: "employee_id",     type: "text" },
    { header: "Employee Name",  key: "employee_name",   type: "text" },
    { header: "Job Role",       key: "job_role",        type: "text" },
    { header: "Job position",   key: "job_position",    type: "text" },
    { header: "Region",         key: "region",          type: "text" },
    { header: "City",           key: "city",            type: "text" },
    { header: "Address",        key: "address",         type: "text" },
    { header: "Join Date",      key: "join_date",       type: "text" },
    { header: "Department",     key: "department",      type: "text" },
    { header: "Office Email",   key: "email",           type: "text" },
    { header: "Personal Email", key: "personal_email",  type: "text" },
    { header: "Office Phone",   key: "number",          type: "text" },
    { header: "Personal Phone", key: "personal_number", type: "text" },
    { header: "Date of Birth",  key: "DOB",             type: "text" },
    { header: "Status",         key: "is_active",       type: "text" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; }
        .emp-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .emp-scrollbar::-webkit-scrollbar-track { background: rgba(241,245,249,1); border-radius: 10px; }
        .emp-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
        .emp-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.8); }
      `}</style>

      <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

        {/* Top accent line */}
        <div
          className="fixed top-0 left-0 right-0 h-0.5 z-50"
          style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
        />

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => console.log("Logout")} />

        {/* ── STICKY HEADER ── */}
        <div
          className="sticky top-0 z-20 border-b border-slate-200"
          style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
        >
          <div className="px-7 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2.5">
                {/* Blue accent bar */}
                <div
                  className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
                />
                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800 flex items-center gap-2">
                  <UserGroupIcon className="w-5 h-5 text-blue-700" />
                  Employees Management
                </h1>
              </div>
            </div>
            <button
              onClick={() => setShowModal({ open: true, data: null })}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white border-0 transition-all"
              style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Add New Employee</span>
            </button>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="relative z-10 p-5 flex flex-col gap-4">

          {/* ── FILTER BAR ── */}
          <div
            className="rounded-xl px-5 py-4 bg-white border border-slate-200"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              {/* Left: stat pills + tabs */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Stat pills */}
                {/* <StatCard
                  label="Total"
                  value={employees.length}
                  colorClass="bg-blue-50 text-blue-700 border border-blue-200"
                  dotClass="bg-blue-500"
                />
                <StatCard
                  label="Active"
                  value={activeCount}
                  colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200"
                  dotClass="bg-emerald-500"
                />
                <StatCard
                  label="Inactive"
                  value={inactiveCount}
                  colorClass="bg-rose-50 text-rose-700 border border-rose-200"
                  dotClass="bg-rose-500"
                /> */}

                {/* Divider */}
                <div className="h-6 w-px bg-slate-200 hidden sm:block" />

                {/* Tabs */}
                <div className="flex gap-1.5">
                  {TABS.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-3 py-1 rounded-lg text-xs font-bold border transition-all duration-200 ${activeTab === tab.id ? tab.active : tab.idle}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: search + export */}
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Search employee..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-52 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                />
                <ExcelExport
                  data={excelData}
                  columns={excelColumns}
                  fileName="EmployeeHistory.xlsx"
                  reportTitle="Employee History"
                />
              </div>
            </div>
          </div>

          {/* ── TABLE ── */}
          <div
            className="rounded-xl overflow-hidden bg-white border border-slate-200"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
          >
            <div className="overflow-x-auto overflow-y-auto max-h-[65vh] emp-scrollbar">
              {loading ? (
                <div className="p-10 text-center text-slate-400 text-sm">
                  <span className="inline-block animate-pulse">Loading employees…</span>
                </div>
              ) : (
                <table className="min-w-[700px] w-full text-sm">

                  {/* thead */}
                  <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                    <tr>
                      <th className="p-3 text-left border-b border-slate-200">
                        <TableHeaderFilter
                          label="Employee ID"
                          options={employeeIdOptions}
                          selectedValues={filterEmployeeId}
                          onChange={setFilterEmployeeId}
                          fullWidth
                        />
                      </th>
                      <th className="p-3 text-left border-b border-slate-200">
                        <TableHeaderFilter
                          label="Name"
                          options={employeeNameOptions}
                          selectedValues={filterEmployeeName}
                          onChange={setFilterEmployeeName}
                          fullWidth
                        />
                      </th>
                      <th className="p-3 text-left border-b border-slate-200">
                        <TableHeaderFilter
                          label="Email"
                          options={emailOptions}
                          selectedValues={filterEmail}
                          onChange={setFilterEmail}
                          fullWidth
                        />
                      </th>
                      <th className="p-3 text-left border-b border-slate-200">
                        <TableHeaderFilter
                          label="Job Role"
                          options={jobRoleOptions}
                          selectedValues={filterJobRole}
                          onChange={setFilterJobRole}
                          fullWidth
                        />
                      </th>
                      <th className="p-3 text-center border-b border-slate-200">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span>
                      </th>
                      <th className="p-3 text-right border-b border-slate-200">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredEmployees.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-400 text-sm">
                          No records matching your search
                        </td>
                      </tr>
                    ) : (
                      filteredEmployees.map((emp) => {
                        const status = Number(emp.is_active) === 1 ? "active" : "inactive";
                        return (
                          <tr
                            key={emp.employee_id}
                            className="border-t border-slate-100 transition-colors hover:bg-blue-50/60 cursor-pointer"
                            onClick={(e) => {
                              if (e.target.closest(".action-cell")) return;
                              setShowModal({ open: true, data: emp });
                            }}
                          >
                            <td className="p-3 font-mono text-xs text-slate-500 font-medium">{emp.employee_id}</td>
                            <td className="p-3 text-slate-800 font-semibold text-xs">{emp.employee_name}</td>
                            <td className="p-3 text-slate-500 text-xs">{emp.email}</td>
                            <td className="p-3">
                              <span className="px-2.5 py-0.5 text-xs rounded-full font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                {emp.job_role}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase
                                ${status === "active"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-rose-50 text-rose-700 border border-rose-200"
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${status === "active" ? "bg-emerald-500" : "bg-rose-400"}`} />
                                {status}
                              </span>
                            </td>
                            <td className="p-3 text-right action-cell">
                              <div
                                onClick={(e) => { e.stopPropagation(); toggleStatus(emp.employee_id, emp.is_active); }}
                                className={`relative inline-flex h-6 w-12 cursor-pointer rounded-full transition-colors duration-300
                                  ${Number(emp.is_active) === 1 ? "bg-emerald-500" : "bg-red-500"}`}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 mt-0.5
                                    ${Number(emp.is_active) === 1 ? "translate-x-6" : "translate-x-1"}`}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>

        {/* Modal */}
        {showModal.open && (
          <AddEmployeeModal
            onClose={() => setShowModal({ open: false, data: null })}
            refreshData={fetchEmployees}
            employeeData={showModal.data}
          />
        )}
      </div>
    </>
  );
};

export default AdminEmployees;